import { useParams, Link } from "wouter";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { MAJOR_ECONOMIES } from "@macroscope/shared";
import { fetchSurpriseScores } from "../lib/api";

type MajorEconomy = (typeof MAJOR_ECONOMIES)[number];

export default function CountryDetail() {
  const { country } = useParams<{ country: string }>();
  const code = (country ?? "").toUpperCase() as MajorEconomy;
  const [flavor, setFlavor] = useState<"trend" | "consensus">("trend");
  const isTracked = MAJOR_ECONOMIES.includes(code);
  const { data, isLoading } = useQuery({
    queryKey: ["surprise", flavor],
    queryFn: () => fetchSurpriseScores(flavor),
    enabled: isTracked,
  });

  const score = useMemo(() => data?.scores.find((s) => s.scope === code), [data, code]);

  if (!isTracked) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="rounded-xl border border-surface-2 bg-surface-1 p-5 text-center">
          <h1 className="text-lg font-semibold">Country not tracked</h1>
          <p className="mt-2 text-xs text-slate-500">{code} is not in the current coverage set.</p>
          <Link href="/surprise" className="mt-4 inline-block text-xs text-macro-accent hover:underline">
            Back to Surprise Engine
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="text-sm text-slate-500">Loading {code} surprise score…</div>;
  }

  if (!score) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="rounded-xl border border-surface-2 bg-surface-1 p-5 text-center">
          <h1 className="text-lg font-semibold">No data for {code}</h1>
          <p className="mt-2 text-xs text-slate-500">No scored releases are available for this country yet.</p>
          <Link href="/surprise" className="mt-4 inline-block text-xs text-macro-accent hover:underline">
            Back to Surprise Engine
          </Link>
        </div>
      </div>
    );
  }

  const value = score.score;
  const label = value > 25 ? "positive surprise bias" : value < -25 ? "negative surprise bias" : "neutral surprise bias";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">{code} Surprise Detail</h1>
            <p className="text-xs text-slate-500">{label}</p>
            {flavor === "consensus" && data?.coverage_gap && (
              <p className="mt-1 text-xs text-yellow-500">{data.coverage_gap}</p>
            )}
          </div>
          <div className="flex rounded-lg border border-surface-2 bg-surface-1 p-1">
            {(["trend", "consensus"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFlavor(f)}
                className={`rounded px-3 py-1 text-xs font-medium capitalize ${
                  flavor === f ? "bg-macro-accent/10 text-macro-accent" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
        <div className="flex items-end gap-3">
          <div className={`font-mono text-5xl font-semibold ${value > 0 ? "text-macro-up" : value < 0 ? "text-macro-down" : "text-slate-300"}`}>
            {value > 0 ? "+" : ""}
            {value.toFixed(1)}
          </div>
          <div className="pb-1 text-sm text-slate-400">{label}</div>
        </div>
        <div className="mt-3 h-2 w-full rounded-full bg-surface-2">
          <div
            className={`h-2 rounded-full ${value >= 0 ? "bg-macro-up" : "bg-macro-down"}`}
            style={{ width: `${Math.min(100, Math.abs(value))}%`, marginLeft: value >= 0 ? "50%" : `${50 - Math.min(100, Math.abs(value))}%` }}
          />
        </div>
        <p className="mt-3 text-xs text-slate-500">
          so-what: {""}
          {value > 25
            ? "Data has mostly beaten trend — growth/inflation momentum may be firmer than priced."
            : value < -25
            ? "Data has mostly missed trend — momentum may be softer than priced."
            : "Releases are mixed vs. trend — no clear macro surprise edge."}
        </p>
      </div>

      <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
        <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Contributors</h2>
        {score.contributors.length === 0 ? (
          <div className="text-sm text-slate-500">No scored releases in the current window.</div>
        ) : (
          <ul className="space-y-2">
            {score.contributors.map((c, i) => (
              <li key={`${c.event_id}-${i}`} className="rounded-lg border border-surface-2 bg-surface-2 px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{c.event_id}</span>
                  <span className={`text-xs font-mono ${c.z > 0 ? "text-macro-up" : "text-macro-down"}`}>
                    {c.z > 0 ? "+" : ""}
                    {c.z.toFixed(2)}σ
                  </span>
                </div>
                <div className="text-[10px] text-slate-500">
                  weight {(c.weight * 100).toFixed(0)}%
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-[10px] text-slate-600">
        Source: {score.source} · {score.updated_at ? new Date(score.updated_at).toLocaleString() : ""}
        {score.delayed && " · delayed"} · educational
      </div>

      <Link href="/surprise" className="inline-block text-xs text-macro-accent hover:underline">
        Back to Surprise Engine
      </Link>
    </div>
  );
}
